import {
  Scene,
  Mesh,
  MeshBuilder,
  StandardMaterial,
  Color3,
  TransformNode,
  Vector3,
  Animation,
  AnimationGroup,
  Texture,
} from '@babylonjs/core';

export interface PlayerColors {
  skin: Color3;
  shirt: Color3;
  pants: Color3;
  shoes: Color3;
}

const DEFAULT_COLORS: PlayerColors = {
  skin: new Color3(1.0, 0.85, 0.75),      // Warm peachy skin
  shirt: new Color3(0.3, 0.7, 0.95),      // Bright sky blue
  pants: new Color3(0.4, 0.55, 0.85),     // Light blue jeans
  shoes: new Color3(0.95, 0.3, 0.4),      // Red sneakers
};

export class PlayerModel {
  private scene: Scene;
  private root: TransformNode;
  private colors: PlayerColors;

  // Body parts
  private head!: Mesh;
  private torso!: Mesh;
  private leftArm!: Mesh;
  private rightArm!: Mesh;
  private leftLeg!: Mesh;
  private rightLeg!: Mesh;

  // Animation
  private walkAnimation: AnimationGroup | null = null;
  private currentAnimation: 'idle' | 'walk' | 'run' = 'idle';
  private animationSpeed: number = 1;

  // Arm pivot points for animation
  private leftArmPivot!: TransformNode;
  private rightArmPivot!: TransformNode;
  private leftLegPivot!: TransformNode;
  private rightLegPivot!: TransformNode;

  constructor(scene: Scene, colors?: Partial<PlayerColors>) {
    this.scene = scene;
    this.colors = { ...DEFAULT_COLORS, ...colors };
    this.root = new TransformNode('PlayerModel', scene);

    this.createModel();
    this.createAnimations();
  }

  private createModel(): void {
    // Dimensions (Roblox-style blocky proportions)
    const headSize = 0.6;
    const torsoWidth = 0.6;
    const torsoHeight = 0.7;
    const torsoDepth = 0.4;
    const armWidth = 0.25;
    const armHeight = 0.65;
    const legWidth = 0.28;
    const legHeight = 0.7;

    // Materials - bright solid colors for Roblox-style cartoon look
    const skinMat = this.createMaterial('skin', this.colors.skin);
    const shirtMat = this.createMaterial('shirt', this.colors.shirt);
    const pantsMat = this.createMaterial('pants', this.colors.pants);
    const shoesMat = this.createMaterial('shoes', this.colors.shoes);

    // HEAD
    this.head = MeshBuilder.CreateBox('head', {
      width: headSize,
      height: headSize,
      depth: headSize,
    }, this.scene);
    this.head.material = skinMat;
    this.head.position.y = torsoHeight / 2 + headSize / 2 + 0.05;
    this.head.parent = this.root;

    // Add face details
    this.addFace();

    // TORSO
    this.torso = MeshBuilder.CreateBox('torso', {
      width: torsoWidth,
      height: torsoHeight,
      depth: torsoDepth,
    }, this.scene);
    this.torso.material = shirtMat;
    this.torso.position.y = 0;
    this.torso.parent = this.root;

    // LEFT ARM (with pivot at shoulder)
    this.leftArmPivot = new TransformNode('leftArmPivot', this.scene);
    this.leftArmPivot.position = new Vector3(-(torsoWidth / 2 + armWidth / 2), torsoHeight / 2 - 0.1, 0);
    this.leftArmPivot.parent = this.root;

    this.leftArm = MeshBuilder.CreateBox('leftArm', {
      width: armWidth,
      height: armHeight,
      depth: armWidth,
    }, this.scene);
    this.leftArm.material = shirtMat;
    this.leftArm.position.y = -armHeight / 2 + 0.1;
    this.leftArm.parent = this.leftArmPivot;

    // RIGHT ARM (with pivot at shoulder)
    this.rightArmPivot = new TransformNode('rightArmPivot', this.scene);
    this.rightArmPivot.position = new Vector3(torsoWidth / 2 + armWidth / 2, torsoHeight / 2 - 0.1, 0);
    this.rightArmPivot.parent = this.root;

    this.rightArm = MeshBuilder.CreateBox('rightArm', {
      width: armWidth,
      height: armHeight,
      depth: armWidth,
    }, this.scene);
    this.rightArm.material = shirtMat;
    this.rightArm.position.y = -armHeight / 2 + 0.1;
    this.rightArm.parent = this.rightArmPivot;

    // LEFT LEG (with pivot at hip)
    this.leftLegPivot = new TransformNode('leftLegPivot', this.scene);
    this.leftLegPivot.position = new Vector3(-legWidth / 2 - 0.02, -torsoHeight / 2, 0);
    this.leftLegPivot.parent = this.root;

    this.leftLeg = MeshBuilder.CreateBox('leftLeg', {
      width: legWidth,
      height: legHeight,
      depth: legWidth,
    }, this.scene);
    this.leftLeg.material = pantsMat;
    this.leftLeg.position.y = -legHeight / 2;
    this.leftLeg.parent = this.leftLegPivot;

    // RIGHT LEG (with pivot at hip)
    this.rightLegPivot = new TransformNode('rightLegPivot', this.scene);
    this.rightLegPivot.position = new Vector3(legWidth / 2 + 0.02, -torsoHeight / 2, 0);
    this.rightLegPivot.parent = this.root;

    this.rightLeg = MeshBuilder.CreateBox('rightLeg', {
      width: legWidth,
      height: legHeight,
      depth: legWidth,
    }, this.scene);
    this.rightLeg.material = pantsMat;
    this.rightLeg.position.y = -legHeight / 2;
    this.rightLeg.parent = this.rightLegPivot;

    // LEFT SHOE (red sneaker)
    const shoeHeight = 0.12;
    const leftShoe = MeshBuilder.CreateBox('leftShoe', {
      width: legWidth + 0.04,
      height: shoeHeight,
      depth: legWidth + 0.08,
    }, this.scene);
    leftShoe.material = shoesMat;
    leftShoe.position.y = -legHeight - shoeHeight / 2 + 0.02;
    leftShoe.position.z = 0.02;
    leftShoe.parent = this.leftLegPivot;

    // RIGHT SHOE (red sneaker)
    const rightShoe = MeshBuilder.CreateBox('rightShoe', {
      width: legWidth + 0.04,
      height: shoeHeight,
      depth: legWidth + 0.08,
    }, this.scene);
    rightShoe.material = shoesMat;
    rightShoe.position.y = -legHeight - shoeHeight / 2 + 0.02;
    rightShoe.position.z = 0.02;
    rightShoe.parent = this.rightLegPivot;

    // Position root so feet are at y=0
    this.root.position.y = legHeight + torsoHeight / 2;
  }

  private addFace(): void {
    // Eyes
    const eyeMat = new StandardMaterial('eyeMat', this.scene);
    eyeMat.diffuseColor = new Color3(0.1, 0.1, 0.1);
    eyeMat.specularColor = new Color3(0.5, 0.5, 0.5);

    const eyeWhiteMat = new StandardMaterial('eyeWhiteMat', this.scene);
    eyeWhiteMat.diffuseColor = new Color3(1, 1, 1);

    // Left eye white
    const leftEyeWhite = MeshBuilder.CreateBox('leftEyeWhite', {
      width: 0.12,
      height: 0.1,
      depth: 0.02,
    }, this.scene);
    leftEyeWhite.material = eyeWhiteMat;
    leftEyeWhite.position = new Vector3(-0.12, 0.08, 0.3);
    leftEyeWhite.parent = this.head;

    // Left pupil
    const leftEye = MeshBuilder.CreateBox('leftEye', {
      width: 0.06,
      height: 0.06,
      depth: 0.025,
    }, this.scene);
    leftEye.material = eyeMat;
    leftEye.position = new Vector3(-0.12, 0.08, 0.31);
    leftEye.parent = this.head;

    // Right eye white
    const rightEyeWhite = MeshBuilder.CreateBox('rightEyeWhite', {
      width: 0.12,
      height: 0.1,
      depth: 0.02,
    }, this.scene);
    rightEyeWhite.material = eyeWhiteMat;
    rightEyeWhite.position = new Vector3(0.12, 0.08, 0.3);
    rightEyeWhite.parent = this.head;

    // Right pupil
    const rightEye = MeshBuilder.CreateBox('rightEye', {
      width: 0.06,
      height: 0.06,
      depth: 0.025,
    }, this.scene);
    rightEye.material = eyeMat;
    rightEye.position = new Vector3(0.12, 0.08, 0.31);
    rightEye.parent = this.head;

    // Smile
    const smileMat = new StandardMaterial('smileMat', this.scene);
    smileMat.diffuseColor = new Color3(0.6, 0.3, 0.3);

    const smile = MeshBuilder.CreateBox('smile', {
      width: 0.2,
      height: 0.04,
      depth: 0.02,
    }, this.scene);
    smile.material = smileMat;
    smile.position = new Vector3(0, -0.1, 0.3);
    smile.parent = this.head;

    // Hair - brown messy kid hair
    const hairMat = new StandardMaterial('hairMat', this.scene);
    hairMat.diffuseColor = new Color3(0.35, 0.2, 0.1); // Brown hair
    hairMat.specularColor = new Color3(0.2, 0.15, 0.1);

    // Main hair block
    const hairMain = MeshBuilder.CreateBox('hairMain', {
      width: 0.58,
      height: 0.15,
      depth: 0.55,
    }, this.scene);
    hairMain.material = hairMat;
    hairMain.position = new Vector3(0, 0.32, -0.02);
    hairMain.parent = this.head;

    // Front hair tuft
    const hairFront = MeshBuilder.CreateBox('hairFront', {
      width: 0.35,
      height: 0.1,
      depth: 0.12,
    }, this.scene);
    hairFront.material = hairMat;
    hairFront.position = new Vector3(0, 0.28, 0.25);
    hairFront.parent = this.head;
  }

  private createMaterial(name: string, color: Color3, texturePath?: string): StandardMaterial {
    const mat = new StandardMaterial(`player_${name}`, this.scene);
    mat.diffuseColor = color;
    mat.specularColor = new Color3(0.1, 0.1, 0.1);

    if (texturePath) {
      const texture = new Texture(texturePath, this.scene);
      texture.uScale = 2;
      texture.vScale = 2;
      mat.diffuseTexture = texture;
    }

    return mat;
  }

  private createAnimations(): void {
    const frameRate = 30;

    // Walk animation for limbs
    this.walkAnimation = new AnimationGroup('walkAnimation', this.scene);

    // Left arm swing
    const leftArmAnim = new Animation(
      'leftArmWalk',
      'rotation.x',
      frameRate,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CYCLE
    );
    leftArmAnim.setKeys([
      { frame: 0, value: -0.5 },
      { frame: 15, value: 0.5 },
      { frame: 30, value: -0.5 },
    ]);

    // Right arm swing (opposite)
    const rightArmAnim = new Animation(
      'rightArmWalk',
      'rotation.x',
      frameRate,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CYCLE
    );
    rightArmAnim.setKeys([
      { frame: 0, value: 0.5 },
      { frame: 15, value: -0.5 },
      { frame: 30, value: 0.5 },
    ]);

    // Left leg swing
    const leftLegAnim = new Animation(
      'leftLegWalk',
      'rotation.x',
      frameRate,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CYCLE
    );
    leftLegAnim.setKeys([
      { frame: 0, value: 0.5 },
      { frame: 15, value: -0.5 },
      { frame: 30, value: 0.5 },
    ]);

    // Right leg swing (opposite)
    const rightLegAnim = new Animation(
      'rightLegWalk',
      'rotation.x',
      frameRate,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CYCLE
    );
    rightLegAnim.setKeys([
      { frame: 0, value: -0.5 },
      { frame: 15, value: 0.5 },
      { frame: 30, value: -0.5 },
    ]);

    this.walkAnimation.addTargetedAnimation(leftArmAnim, this.leftArmPivot);
    this.walkAnimation.addTargetedAnimation(rightArmAnim, this.rightArmPivot);
    this.walkAnimation.addTargetedAnimation(leftLegAnim, this.leftLegPivot);
    this.walkAnimation.addTargetedAnimation(rightLegAnim, this.rightLegPivot);
  }

  public playWalk(speed: number = 1): void {
    if (this.currentAnimation !== 'walk' || this.animationSpeed !== speed) {
      this.stopAnimations();
      this.currentAnimation = 'walk';
      this.animationSpeed = speed;

      if (this.walkAnimation) {
        this.walkAnimation.speedRatio = speed;
        this.walkAnimation.play(true);
      }
    }
  }

  public playRun(): void {
    this.playWalk(2);
    this.currentAnimation = 'run';
  }

  public playIdle(): void {
    if (this.currentAnimation !== 'idle') {
      this.stopAnimations();
      this.currentAnimation = 'idle';

      // Reset limb rotations
      this.leftArmPivot.rotation.x = 0;
      this.rightArmPivot.rotation.x = 0;
      this.leftLegPivot.rotation.x = 0;
      this.rightLegPivot.rotation.x = 0;
    }
  }

  private stopAnimations(): void {
    if (this.walkAnimation) {
      this.walkAnimation.stop();
    }
  }

  public setPosition(position: Vector3): void {
    this.root.position = position.clone();
    this.root.position.y += 1.05; // Adjust for model height
  }

  public setRotation(yRotation: number): void {
    this.root.rotation.y = yRotation;
  }

  public getPosition(): Vector3 {
    return this.root.position.clone();
  }

  public getRoot(): TransformNode {
    return this.root;
  }

  public getHeadPosition(): Vector3 {
    return this.head.getAbsolutePosition();
  }

  public setVisible(visible: boolean): void {
    this.root.setEnabled(visible);
  }

  public setColors(colors: Partial<PlayerColors>): void {
    if (colors.skin) {
      (this.head.material as StandardMaterial).diffuseColor = colors.skin;
    }
    if (colors.shirt) {
      (this.torso.material as StandardMaterial).diffuseColor = colors.shirt;
      (this.leftArm.material as StandardMaterial).diffuseColor = colors.shirt;
      (this.rightArm.material as StandardMaterial).diffuseColor = colors.shirt;
    }
    if (colors.pants) {
      (this.leftLeg.material as StandardMaterial).diffuseColor = colors.pants;
      (this.rightLeg.material as StandardMaterial).diffuseColor = colors.pants;
    }
  }

  public dispose(): void {
    this.stopAnimations();
    this.walkAnimation?.dispose();
    this.root.dispose();
  }
}
